import { useState } from 'react';

// ============================================================
// ★ MOCKUP UI SAHAJA — "HOOK BANK" (seksyen dalam PRO Studio)
// Tiada logic sebenar: TIADA Firestore, TIADA localStorage,
// TIADA kesan pada Generate. State lokal je (hilang bila reload).
// Dirender admin-only (via ProStudioView) → selamat walaupun terdeploy.
//
// Konsep (dikunci dengan user 2026-07-26):
//   • User cipta GROUP — nama + senarai contoh hook.
//   • Setiap Generate, sistem cabut SATU contoh secara RAWAK.
//   • Group dikongsi SEMUA module (beza dari seksyen Gaya yang per-module).
//   • Dua cara guna per group:
//       - adapt    : AI tiru gaya contoh, tulis ayat baru ikut produk
//       - verbatim : contoh ditampal bulat-bulat, AI tak sentuh (kos AI sifar)
//   • Kuota ikut lesen — mekanik sama maxProfiles (avatar + profil Chrome):
//       Premium 3 · Fighter 6.
// ============================================================

const MAX_GROUPS = 3;      // demo = kuota Premium; Fighter 6 (ikut maxProfiles lesen)
const HOOK_MAXLEN = 90;    // panduan lembut — hook patut ayat pendek

const USAGE = {
  adapt: {
    label: 'Sesuaikan dengan produk',
    tag: 'AI ADAPT',
    icon: 'auto_awesome',
    short: 'AI tiru gaya',
    desc: 'AI ambil satu contoh, tiru gayanya, tapi tulis ayat baru yang padan dengan produk semasa.',
    forWho: 'Untuk anda yang mahu beri arah dan nada, tapi biar AI yang menulis.',
  },
  verbatim: {
    label: 'Guna terus, tanpa ubah',
    tag: 'COP TERUS',
    icon: 'content_paste',
    short: 'Tampal bulat-bulat',
    desc: 'Contoh ditampal terus sebagai hook. AI langsung tak sentuh — satu huruf pun tak berubah.',
    forWho: 'Untuk anda yang dah ada hook terbukti dan tak mahu sesiapa ubah.',
  },
};

const mkHook = (text) => ({ id: `h_${Math.random().toString(36).slice(2, 8)}`, text });

// Demo data — nanti dari Firestore users/{uid}
const initialGroups = () => ([
  {
    id: 'g1',
    name: 'Hook Mak-Mak Urgency',
    usage: 'adapt',
    nota: 'Nada mendesak tapi mesra. Ayat pendek, macam cakap dengan kawan. Boleh guna emoji.',
    hooks: [
      mkHook('Hari ni je murah, esok dah naik!'),
      mkHook('Jangan beli kalau tak nak ketagih 😩'),
      mkHook('Aku dah try, memang berbaloi'),
      mkHook('Last day promo — lepas ni habis!'),
    ],
  },
  {
    id: 'g2',
    name: 'Hook Simpanan Aku',
    usage: 'verbatim',
    nota: '',
    hooks: [
      mkHook('Korang tengok ni dulu sebelum beli 👀'),
      mkHook('Ni yang aku pakai setiap hari'),
      mkHook('Serius, aku tak sangka jadi macam ni'),
    ],
  },
]);

// ── gaya kongsi (selaras ProStudioAgentSection) ──
const card = { background: '#ffffff', border: '1px solid #e8edf4', borderRadius: 12, padding: 14 };
const lbl = { fontSize: 10, fontWeight: 800, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '8px 10px', fontSize: 11.5, fontWeight: 600, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#1e293b', outline: 'none', boxSizing: 'border-box' };
const hint = { fontSize: 9.5, color: '#94a3b8', fontWeight: 500, marginTop: 4, lineHeight: 1.5 };

export default function ProStudioHookSection() {
  const [groups, setGroups] = useState(initialGroups);
  const [selectedId, setSelectedId] = useState('g1');
  const [defaultId, setDefaultId] = useState('g2');   // ⭐ Default Peribadi
  const [dirtyIds, setDirtyIds] = useState({});
  const [savedFlash, setSavedFlash] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');

  const group = groups.find(g => g.id === selectedId) || null;
  const quotaFull = groups.length >= MAX_GROUPS;
  const isDirty = !!dirtyIds[selectedId];

  const patchGroup = (patch) => {
    setGroups(prev => prev.map(g => (g.id === selectedId ? { ...g, ...patch } : g)));
    setDirtyIds(prev => ({ ...prev, [selectedId]: true }));
  };

  const selectGroup = (id) => {
    setSelectedId(id);
    setBulkOpen(false);
    setBulkText('');
  };

  const newGroup = () => {
    if (quotaFull) return;
    let n = 1;
    while (groups.some(g => g.name === `Group Baru ${n}`)) n++;
    const g = { id: `g_${Math.random().toString(36).slice(2, 8)}`, name: `Group Baru ${n}`, usage: 'adapt', nota: '', hooks: [] };
    setGroups(prev => [...prev, g]);
    setDirtyIds(prev => ({ ...prev, [g.id]: true }));
    selectGroup(g.id);
  };

  const deleteGroup = (id) => {
    setGroups(prev => prev.filter(g => g.id !== id));
    setDirtyIds(prev => { const n = { ...prev }; delete n[id]; return n; });
    if (defaultId === id) setDefaultId('');
    if (selectedId === id) selectGroup(groups.find(g => g.id !== id)?.id || '');
  };

  const saveCurrent = () => {
    if (!group) return;
    setDirtyIds(prev => { const n = { ...prev }; delete n[selectedId]; return n; });
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  // ── Contoh hook ──
  const setHookText = (hid, text) =>
    patchGroup({ hooks: group.hooks.map(h => (h.id === hid ? { ...h, text } : h)) });

  const addHook = () => patchGroup({ hooks: [...group.hooks, mkHook('')] });

  const removeHook = (hid) => patchGroup({ hooks: group.hooks.filter(h => h.id !== hid) });

  const bulkLines = bulkText.split('\n').map(s => s.trim()).filter(Boolean);

  const applyBulk = () => {
    if (!bulkLines.length) return;
    patchGroup({ hooks: [...group.hooks, ...bulkLines.map(mkHook)] });
    setBulkText('');
    setBulkOpen(false);
  };

  const filled = group ? group.hooks.filter(h => h.text.trim()).length : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ═══ Nota skop — Group dikongsi SEMUA module ═══ */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        background: 'linear-gradient(135deg,#eef2ff 0%,#f5f3ff 100%)',
        border: '1px solid #c7d2fe', borderRadius: 12, padding: '10px 14px',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 17, color: '#6366f1', flexShrink: 0 }}>public</span>
        <div style={{ fontSize: 11, color: '#4338ca', fontWeight: 600, lineHeight: 1.55 }}>
          Group hook boleh diguna di <b>semua module</b> — cipta sekali, pakai di mana-mana.
          <span style={{ color: '#6366f1', fontWeight: 500 }}> Pilih group yang nak dipakai di Workspace, dalam kotak Mode PRO.</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* ═══ Senarai Group (kiri) ═══ */}
        <div style={{ ...card, flex: '0 0 225px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Group Anda</span>
            <span style={{
              fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4,
              color: quotaFull ? '#ef4444' : '#6366f1',
              background: quotaFull ? '#fef2f2' : '#f5f3ff',
              border: `1px solid ${quotaFull ? '#fee2e2' : '#e0e7ff'}`,
            }}>{groups.length}/{MAX_GROUPS}</span>
          </div>

          {groups.length === 0 && (
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, padding: '10px 0', textAlign: 'center' }}>
              Belum ada group.
            </div>
          )}

          {groups.map(g => {
            const active = selectedId === g.id;
            const isDefault = defaultId === g.id;
            const u = USAGE[g.usage];
            const count = g.hooks.filter(h => h.text.trim()).length;
            return (
              <div
                key={g.id}
                onClick={() => selectGroup(g.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 9px',
                  borderRadius: 9, cursor: 'pointer',
                  background: active ? '#eef2ff' : '#f8fafc',
                  border: `1.5px solid ${active ? '#6366f1' : '#f1f5f9'}`,
                  transition: 'all 0.15s',
                }}
              >
                {/* ⭐ Default Peribadi — auto terpilih setiap buka Workspace */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setDefaultId(isDefault ? '' : g.id); }}
                  title={isDefault ? 'Default Peribadi anda — auto terpilih di Workspace' : 'Jadikan Default Peribadi (auto terpilih setiap kali buka Workspace)'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                >
                  <span className="material-symbols-outlined" style={{
                    fontSize: 16, color: isDefault ? '#f59e0b' : '#cbd5e1',
                    fontVariationSettings: isDefault ? "'FILL' 1" : "'FILL' 0",
                  }}>star</span>
                </button>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: active ? '#4338ca' : '#334155',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    {g.name}
                    {dirtyIds[g.id] && <span title="Ada perubahan belum disimpan" style={{ width: 5, height: 5, borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />}
                  </div>
                  <div style={{ fontSize: 8.5, color: '#94a3b8', fontWeight: 600 }}>
                    {u.short} · {count} contoh{isDefault ? ' · ⭐' : ''}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); deleteGroup(g.id); }}
                  title="Padam group"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#cbd5e1', display: 'flex', alignItems: 'center' }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
                </button>
              </div>
            );
          })}

          <button
            type="button"
            disabled={quotaFull}
            onClick={newGroup}
            style={{
              padding: '8px 10px', borderRadius: 9, fontSize: 11, fontWeight: 800,
              cursor: quotaFull ? 'not-allowed' : 'pointer',
              background: '#ffffff',
              border: `1px dashed ${quotaFull ? '#e2e8f0' : '#c7d2fe'}`,
              color: quotaFull ? '#cbd5e1' : '#6366f1',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>add</span>
            {quotaFull ? 'Slot Penuh' : 'Group Baru'}
          </button>

          <div style={{ fontSize: 9, color: '#94a3b8', lineHeight: 1.5, paddingTop: 4, borderTop: '1px solid #f1f5f9' }}>
            ⭐ = Default Peribadi — group ini terpilih automatik setiap kali anda buka Workspace.
            <br />
            <span style={{ color: '#cbd5e1' }}>Kuota ikut lesen: Premium 3 · Fighter 6.</span>
          </div>
        </div>

        {/* ═══ Editor (kanan) ═══ */}
        <div style={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

          {!group && (
            <div style={{ ...card, padding: '48px 24px', textAlign: 'center', borderStyle: 'dashed', borderColor: '#e2e8f0' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 40, color: '#cbd5e1' }}>anchor</span>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#64748b', marginTop: 8 }}>Tiada group dipilih</div>
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>Cipta group baru untuk mula simpan hook anda.</div>
            </div>
          )}

          {group && (
            <>
              {/* ── Bar: nama group + simpan ── */}
              <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#6366f1' }}>anchor</span>
                <input
                  type="text"
                  value={group.name}
                  onChange={(e) => patchGroup({ name: e.target.value })}
                  placeholder="Nama group"
                  style={{ ...inp, flex: '1 1 180px', minWidth: 0, width: 'auto', fontSize: 12.5, fontWeight: 800, border: '1px solid transparent', background: '#f8fafc' }}
                />
                {isDirty && (
                  <span style={{ fontSize: 9, fontWeight: 800, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                    BELUM SIMPAN
                  </span>
                )}
                <button
                  type="button"
                  onClick={saveCurrent}
                  style={{
                    padding: '7px 14px', borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: 'pointer',
                    background: savedFlash ? '#10b981' : '#6366f1', border: 'none', color: '#ffffff',
                    display: 'flex', alignItems: 'center', gap: 5, transition: 'background 0.2s',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{savedFlash ? 'check' : 'save'}</span>
                  {savedFlash ? 'Disimpan' : 'Simpan'}
                </button>
              </div>

              {/* ── Cara guna — dua kad pilihan ── */}
              <div style={card}>
                <span style={lbl}>Cara Guna</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
                  {Object.entries(USAGE).map(([key, u]) => {
                    const on = group.usage === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => patchGroup({ usage: key })}
                        style={{
                          textAlign: 'left', cursor: 'pointer', padding: 11, borderRadius: 10,
                          background: on ? '#eef2ff' : '#ffffff',
                          border: `1.5px solid ${on ? '#6366f1' : '#e2e8f0'}`,
                          display: 'flex', flexDirection: 'column', gap: 5, transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 15, color: on ? '#6366f1' : '#94a3b8' }}>{u.icon}</span>
                          <span style={{ fontSize: 11.5, fontWeight: 800, color: on ? '#4338ca' : '#334155', flex: 1 }}>{u.label}</span>
                          {on && <span className="material-symbols-outlined" style={{ fontSize: 15, color: '#6366f1' }}>check_circle</span>}
                        </div>
                        <div style={{ fontSize: 10, color: on ? '#6366f1' : '#94a3b8', fontWeight: 500, lineHeight: 1.5 }}>{u.desc}</div>
                        {key === 'verbatim' && (
                          <div style={{ fontSize: 9, fontWeight: 800, color: '#059669', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 5, padding: '2px 6px', alignSelf: 'flex-start' }}>
                            KOS AI SIFAR
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div style={hint}>{USAGE[group.usage].forWho}</div>
              </div>

              {/* ── Nota gaya — hanya untuk cara "Sesuaikan dengan produk" ── */}
              {group.usage === 'adapt' && (
                <div style={card}>
                  <span style={lbl}>Nota Gaya <span style={{ color: '#cbd5e1', fontWeight: 600 }}>(pilihan)</span></span>
                  <textarea
                    value={group.nota}
                    onChange={(e) => patchGroup({ nota: e.target.value })}
                    placeholder="Contoh: Nada mendesak tapi mesra. Ayat pendek, macam cakap dengan kawan."
                    style={{ ...inp, minHeight: 52, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5, fontWeight: 500 }}
                  />
                  <div style={hint}>Terangkan nada yang anda mahu. AI akan ikut nota ini bersama contoh di bawah.</div>
                </div>
              )}

              {/* ── Senarai contoh hook ── */}
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ ...lbl, marginBottom: 0 }}>Contoh Hook</span>
                  <span style={{ fontSize: 9, fontWeight: 800, color: '#6366f1', background: '#f5f3ff', border: '1px solid #e0e7ff', padding: '2px 6px', borderRadius: 4 }}>
                    {filled} contoh
                  </span>
                </div>

                {group.hooks.length === 0 && !bulkOpen && (
                  <div style={{
                    padding: '18px 12px', textAlign: 'center', borderRadius: 10,
                    background: '#fffbeb', border: '1px dashed #fde68a',
                    fontSize: 10.5, color: '#92600a', fontWeight: 600, lineHeight: 1.5, marginBottom: 8,
                  }}>
                    Group ini belum ada contoh hook.
                    <br />
                    <span style={{ fontWeight: 500 }}>Tambah sekurang-kurangnya satu contoh untuk mula guna.</span>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {group.hooks.map((h, i) => {
                    const len = h.text.length;
                    const over = len > HOOK_MAXLEN;
                    return (
                      <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          flexShrink: 0, width: 20, textAlign: 'center', fontSize: 9, fontWeight: 800,
                          color: '#94a3b8', fontVariantNumeric: 'tabular-nums',
                        }}>{String(i + 1).padStart(2, '0')}</span>
                        <input
                          type="text"
                          value={h.text}
                          onChange={(e) => setHookText(h.id, e.target.value)}
                          placeholder="Tulis satu ayat hook…"
                          style={{ ...inp, flex: 1, minWidth: 0, fontWeight: 600, borderColor: over ? '#fdba74' : '#e2e8f0' }}
                        />
                        <span style={{
                          flexShrink: 0, width: 40, textAlign: 'right', fontSize: 9, fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          color: over ? '#ea580c' : len > 0 ? '#cbd5e1' : 'transparent',
                        }}>{len}/{HOOK_MAXLEN}</span>
                        <button
                          type="button"
                          onClick={() => removeHook(h.id)}
                          title="Buang contoh ini"
                          style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#cbd5e1', display: 'flex', alignItems: 'center' }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>close</span>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Tambah — satu-satu ATAU tampal banyak sekali gus */}
                {!bulkOpen && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={addHook}
                      style={{
                        flex: '1 1 140px', padding: '8px 10px', borderRadius: 9, fontSize: 11, fontWeight: 800, cursor: 'pointer',
                        background: '#ffffff', border: '1px dashed #c7d2fe', color: '#6366f1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>add</span>
                      Tambah Contoh
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkOpen(true)}
                      title="Tampal banyak hook sekali gus — satu ayat satu baris"
                      style={{
                        flex: '1 1 140px', padding: '8px 10px', borderRadius: 9, fontSize: 11, fontWeight: 800, cursor: 'pointer',
                        background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_paste_go</span>
                      Tampal Banyak
                    </button>
                  </div>
                )}

                {/* Panel tampal banyak */}
                {bulkOpen && (
                  <div style={{
                    marginTop: 10, padding: 10, borderRadius: 10,
                    background: '#f5f3ff', border: '1px dashed #c7d2fe',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#4338ca' }}>Tampal banyak hook — satu ayat satu baris</div>
                    <textarea
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      autoFocus
                      placeholder={'Hari ni je murah, esok dah naik!\nLast day promo — lepas ni habis!\nAku dah try, memang berbaloi'}
                      style={{ ...inp, minHeight: 88, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, fontWeight: 500 }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 700, color: '#6366f1', flex: 1 }}>
                        {bulkLines.length ? `${bulkLines.length} baris dikesan` : 'Belum ada baris'}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setBulkOpen(false); setBulkText(''); }}
                        style={{ padding: '6px 12px', borderRadius: 8, fontSize: 10.5, fontWeight: 700, cursor: 'pointer', background: '#ffffff', border: '1px solid #e2e8f0', color: '#64748b' }}
                      >Batal</button>
                      <button
                        type="button"
                        disabled={!bulkLines.length}
                        onClick={applyBulk}
                        style={{
                          padding: '6px 12px', borderRadius: 8, fontSize: 10.5, fontWeight: 800,
                          cursor: bulkLines.length ? 'pointer' : 'not-allowed',
                          background: bulkLines.length ? '#6366f1' : '#e2e8f0', border: 'none', color: '#ffffff',
                        }}
                      >Masukkan {bulkLines.length || ''}</button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Pratonton: apa jadi masa Generate ── */}
              <div style={{
                background: '#ffffff', border: '1px solid #e8edf4', borderRadius: 12,
                padding: '11px 14px', display: 'flex', alignItems: 'flex-start', gap: 9,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 17, color: '#8b5cf6', flexShrink: 0 }}>casino</span>
                <div style={{ fontSize: 10.5, color: '#475569', fontWeight: 600, lineHeight: 1.6 }}>
                  {filled === 0 && (
                    <span style={{ color: '#b45309' }}>Belum ada contoh. Generate akan guna tetapan asal SESAAT.</span>
                  )}
                  {filled === 1 && (
                    <>Setiap kali Generate: <b style={{ color: '#6366f1' }}>guna contoh yang sama</b>. Tambah lagi contoh supaya hook anda berbeza-beza.</>
                  )}
                  {filled > 1 && (
                    <>Setiap kali Generate: sistem ambil <b style={{ color: '#6366f1' }}>1 daripada {filled} contoh</b> secara rawak.</>
                  )}
                  {filled > 0 && (
                    <div style={{ marginTop: 3, color: '#94a3b8', fontWeight: 500 }}>
                      {group.usage === 'adapt'
                        ? 'AI akan tiru gaya contoh itu, tapi tulis ayat baru ikut produk semasa.'
                        : 'Contoh itu ditampal bulat-bulat sebagai hook — AI tidak menyentuhnya.'}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
